import type { ExerciseDef } from './types'

// ─── 1.1 Values, variables & types ────────────────────────────────────────────

export const valueKinds: ExerciseDef = {
  starter: `// Three basic kinds of value:
console.log(42)               // a number
console.log(3.14)             // also a number (decimals are fine)
console.log("forty-two")      // a string: text, inside quotes
console.log(true)             // a boolean: true or false

// typeof tells you what kind a value is
console.log(typeof 42, typeof "hi", typeof false)
`,
}

export const boxes: ExerciseDef = {
  starter: `const name = "Ada"        // const: this box never changes
let score = 10            // let: this box can be refilled

console.log(name, "has", score, "points")

score = score + 5         // take what's in the box, add 5, put it back
console.log(name, "now has", score, "points")
`,
}

export const typeCheck: ExerciseDef = {
  starter: `let age: number = 36
age = "thirty-six"   // ← hover the red squiggle: TypeScript catches the mix-up

let city: string = "Lagos"
console.log(age, city)
`,
  typeError: true,
}

export const receipt: ExerciseDef = {
  starter: `const price = 12.5
const quantity = 4

// 1. Make total equal price times quantity.
const total = 0

// 2. Make label equal "Total: 50" — but built from total, not typed in by hand.
//    Tip: \`Total: \${total}\` puts a value inside text.
const label = ""

console.log(label)
`,
  solution: `const price = 12.5
const quantity = 4

const total = price * quantity
const label = \`Total: \${total}\`

console.log(label)
`,
  tests: `
test("total is price × quantity", () => expect(total, "total").toBe(price * quantity))
test('label reads "Total: 50"', () => expect(label, "label").toBe("Total: 50"))
`,
}

export const logic: ExerciseDef = {
  starter: `const age = 20
const hasTicket = true
const isVip = false

// canEnter should be true when the person is at least 18 AND has a ticket.
const canEnter = false

// skipQueue should be true when the person is a VIP OR older than 65.
const skipQueue = true

console.log({ canEnter, skipQueue })
`,
  solution: `const age = 20
const hasTicket = true
const isVip = false

const canEnter = age >= 18 && hasTicket
const skipQueue = isVip || age > 65

console.log({ canEnter, skipQueue })
`,
  tests: `
test("canEnter is true for a 20-year-old with a ticket", () => expect(canEnter, "canEnter").toBe(true))
test("skipQueue is false (not a VIP, not over 65)", () => expect(skipQueue, "skipQueue").toBe(false))
`,
}

// ─── 1.2 Functions ────────────────────────────────────────────────────────────

export const firstFunction: ExerciseDef = {
  starter: `// A function: a named machine. x goes in, x * 2 comes out.
function double(x: number): number {
  return x * 2
}

console.log(double(4))
console.log(double(10))
console.log(double(double(3)))   // the output of one call is the input of the next

// The same machine, written as an "arrow function":
const triple = (x: number) => x * 3
console.log(triple(5))
`,
}

export const square: ExerciseDef = {
  starter: `// Write a function called square that returns x times x.
function square(x: number): number {
  return 0
}

// Write celsiusToFahrenheit: multiply by 9, divide by 5, then add 32.
function celsiusToFahrenheit(c: number): number {
  return 0
}

console.log(square(7), celsiusToFahrenheit(100))
`,
  solution: `function square(x: number): number {
  return x * x
}

function celsiusToFahrenheit(c: number): number {
  return (c * 9) / 5 + 32
}

console.log(square(7), celsiusToFahrenheit(100))
`,
  tests: `
test("square(7) is 49", () => expect(square(7), "square(7)").toBe(49))
test("square(-3) is 9", () => expect(square(-3), "square(-3)").toBe(9))
test("water boils at 212°F", () => expect(celsiusToFahrenheit(100), "celsiusToFahrenheit(100)").toBeCloseTo(212))
test("water freezes at 32°F", () => expect(celsiusToFahrenheit(0), "celsiusToFahrenheit(0)").toBeCloseTo(32))
test("−40 is the same in both", () => expect(celsiusToFahrenheit(-40), "celsiusToFahrenheit(-40)").toBeCloseTo(-40))
`,
}

export const ifElse: ExerciseDef = {
  starter: `// "if" lets a function make decisions.
function describe(temp: number): string {
  if (temp > 25) {
    return "hot"
  } else if (temp > 10) {
    return "mild"
  } else {
    return "cold"
  }
}

console.log(describe(30), describe(18), describe(-2))
`,
}

export const absClamp: ExerciseDef = {
  starter: `// 1. abs(x): the distance from 0 — always positive. abs(-5) is 5, abs(3) is 3.
//    (Don't use Math.abs — build it with an if.)
function abs(x: number): number {
  return x
}

// 2. clamp(x, lo, hi): keep x inside [lo, hi].
//    clamp(15, 0, 10) → 10,  clamp(-3, 0, 10) → 0,  clamp(4, 0, 10) → 4
function clamp(x: number, lo: number, hi: number): number {
  return x
}

console.log(abs(-5), clamp(15, 0, 10))
`,
  solution: `function abs(x: number): number {
  if (x < 0) return -x
  return x
}

function clamp(x: number, lo: number, hi: number): number {
  if (x < lo) return lo
  if (x > hi) return hi
  return x
}

console.log(abs(-5), clamp(15, 0, 10))
`,
  tests: `
test("abs(-5) is 5", () => expect(abs(-5), "abs(-5)").toBe(5))
test("abs(3) is 3", () => expect(abs(3), "abs(3)").toBe(3))
test("abs(0) is 0", () => expect(abs(0), "abs(0)").toBe(0))
test("clamp(15, 0, 10) is 10", () => expect(clamp(15, 0, 10), "clamp(15, 0, 10)").toBe(10))
test("clamp(-3, 0, 10) is 0", () => expect(clamp(-3, 0, 10), "clamp(-3, 0, 10)").toBe(0))
test("clamp(4, 0, 10) is 4", () => expect(clamp(4, 0, 10), "clamp(4, 0, 10)").toBe(4))
`,
}

export const fnValues: ExerciseDef = {
  starter: `// Functions are values too: you can pass one to another function.
const f = (x: number) => x * x - 2
const g = (x: number) => 2 * x + 1

plot.fn([f, g], [-3, 3], { title: "two machines, drawn" })

// Composing: feed g's output into f
const fOfG = (x: number) => f(g(x))
console.log("f(g(1)) =", fOfG(1))
`,
}

// ─── 1.3 Lists & loops ────────────────────────────────────────────────────────

export const arraysIntro: ExerciseDef = {
  starter: `const temps = [18, 21, 25, 19, 23]

console.log(temps[0])          // the FIRST item is at index 0
console.log(temps[2])          // the third item
console.log(temps.length)      // how many items
console.log(temps[temps.length - 1])  // the last item

temps.push(30)                 // add one to the end
console.log(temps)
`,
}

export const sumMax: ExerciseDef = {
  starter: `// sum([1, 2, 3]) → 6
function sum(xs: number[]): number {
  let total = 0
  // loop over xs and add each one to total
  return total
}

// biggest([3, 9, 2]) → 9   (assume at least one number)
function biggest(xs: number[]): number {
  let best = xs[0]
  // loop and keep the largest you've seen
  return best
}

console.log(sum([1, 2, 3]), biggest([3, 9, 2]))
`,
  solution: `function sum(xs: number[]): number {
  let total = 0
  for (let i = 0; i < xs.length; i++) {
    total = total + xs[i]
  }
  return total
}

function biggest(xs: number[]): number {
  let best = xs[0]
  for (const x of xs) {
    if (x > best) best = x
  }
  return best
}

console.log(sum([1, 2, 3]), biggest([3, 9, 2]))
`,
  tests: `
test("sum([1, 2, 3]) is 6", () => expect(sum([1, 2, 3]), "sum([1, 2, 3])").toBe(6))
test("sum([]) is 0", () => expect(sum([]), "sum([])").toBe(0))
test("sum works with decimals and negatives", () => expect(sum([2.5, -1, 0.5]), "sum([2.5, -1, 0.5])").toBeCloseTo(2))
test("biggest([3, 9, 2]) is 9", () => expect(biggest([3, 9, 2]), "biggest([3, 9, 2])").toBe(9))
test("biggest works with negatives", () => expect(biggest([-4, -2, -9]), "biggest([-4, -2, -9])").toBe(-2))
test("biggest finds a max at the end", () => expect(biggest([1, 2, 3, 100]), "biggest([1, 2, 3, 100])").toBe(100))
`,
}

export const mapFilter: ExerciseDef = {
  starter: `const xs = [1, 2, 3, 4, 5, 6]

// map: make a new array by transforming every item
console.log(xs.map(x => x * 10))

// filter: keep only items that pass a test
console.log(xs.filter(x => x % 2 === 0))   // % is "remainder": even numbers leave 0

// reduce: boil an array down to one value
console.log(xs.reduce((total, x) => total + x, 0))
`,
}

export const squaresCount: ExerciseDef = {
  starter: `// squares([1, 2, 3]) → [1, 4, 9]   (use .map)
function squares(xs: number[]): number[] {
  return []
}

// countAbove([5, 1, 8, 3], 4) → 2    (how many items are greater than t?)
function countAbove(xs: number[], t: number): number {
  return 0
}

console.log(squares([1, 2, 3]), countAbove([5, 1, 8, 3], 4))
`,
  solution: `function squares(xs: number[]): number[] {
  return xs.map(x => x * x)
}

function countAbove(xs: number[], t: number): number {
  return xs.filter(x => x > t).length
}

console.log(squares([1, 2, 3]), countAbove([5, 1, 8, 3], 4))
`,
  tests: `
test("squares([1, 2, 3]) is [1, 4, 9]", () => expect(squares([1, 2, 3]), "squares([1, 2, 3])").toEqual([1, 4, 9]))
test("squares([]) is []", () => expect(squares([]), "squares([])").toEqual([]))
test("countAbove([5, 1, 8, 3], 4) is 2", () => expect(countAbove([5, 1, 8, 3], 4), "countAbove([5, 1, 8, 3], 4)").toBe(2))
test("countAbove counts strictly greater", () => expect(countAbove([4, 4, 5], 4), "countAbove([4, 4, 5], 4)").toBe(1))
`,
}

export const grid: ExerciseDef = {
  starter: `// A list of lists is a grid. You'll use these for images and matrices.
const grid = [
  [1, 2, 3],
  [4, 5, 6],
]
console.log(grid[1][2])     // row 1, column 2 → 6

// Write total2d: add up every number in a grid.
function total2d(g: number[][]): number {
  let total = 0
  // you'll need a loop inside a loop
  return total
}

console.log(total2d(grid))
`,
  solution: `const grid = [
  [1, 2, 3],
  [4, 5, 6],
]

function total2d(g: number[][]): number {
  let total = 0
  for (const row of g) {
    for (const x of row) total += x
  }
  return total
}

console.log(total2d(grid))
`,
  tests: `
test("total2d of the example is 21", () => expect(total2d([[1, 2, 3], [4, 5, 6]]), "total2d(grid)").toBe(21))
test("total2d handles ragged rows", () => expect(total2d([[1], [2, 3], []]), "total2d([[1], [2, 3], []])").toBe(6))
`,
}

// ─── 1.4 Objects & classes ────────────────────────────────────────────────────

export const objectsIntro: ExerciseDef = {
  starter: `// An object bundles named values together.
const house = { size: 120, rooms: 3, price: 250000 }
console.log(house.size, house.price)

// Datasets are usually arrays of objects:
const houses = [
  { size: 60, price: 140000 },
  { size: 95, price: 205000 },
  { size: 120, price: 250000 },
  { size: 150, price: 330000 },
]
plot.scatter(houses.map(h => [h.size, h.price]), { title: "size vs price", xLabel: "m²", yLabel: "price" })
`,
}

export const meanOf: ExerciseDef = {
  starter: `type House = { size: number; price: number }

const houses: House[] = [
  { size: 60, price: 140000 },
  { size: 95, price: 205000 },
  { size: 120, price: 250000 },
  { size: 150, price: 330000 },
]

// averagePrice(houses) → the mean price
function averagePrice(hs: House[]): number {
  return 0
}

// biggestHouse(houses) → the whole object with the largest size
function biggestHouse(hs: House[]): House {
  return hs[0]
}

console.log(averagePrice(houses), biggestHouse(houses))
`,
  solution: `type House = { size: number; price: number }

const houses: House[] = [
  { size: 60, price: 140000 },
  { size: 95, price: 205000 },
  { size: 120, price: 250000 },
  { size: 150, price: 330000 },
]

function averagePrice(hs: House[]): number {
  let total = 0
  for (const h of hs) total += h.price
  return total / hs.length
}

function biggestHouse(hs: House[]): House {
  let best = hs[0]
  for (const h of hs) if (h.size > best.size) best = h
  return best
}

console.log(averagePrice(houses), biggestHouse(houses))
`,
  tests: `
test("averagePrice is 231250", () => expect(averagePrice(houses), "averagePrice(houses)").toBeCloseTo(231250))
test("averagePrice of one house is its price", () => expect(averagePrice([{ size: 1, price: 7 }]), "averagePrice([...])").toBeCloseTo(7))
test("biggestHouse returns the 150 m² house", () => expect(biggestHouse(houses).size, "biggestHouse(houses).size").toBe(150))
`,
}

export const classIntro: ExerciseDef = {
  starter: `// A class is a blueprint for objects that carry data AND behaviour.
class Counter {
  count = 0

  increment() {
    this.count = this.count + 1   // "this" means "the object this was called on"
  }
}

const a = new Counter()
const b = new Counter()
a.increment()
a.increment()
b.increment()
console.log(a.count, b.count)   // each object has its own count
`,
}

export const pointClass: ExerciseDef = {
  starter: `class Point {
  x: number
  y: number

  // constructor runs when you write new Point(3, 4)
  constructor(x: number, y: number) {
    this.x = x
    this.y = y
  }

  // 1. distanceTo(other): straight-line distance between two points.
  //    Pythagoras: √(dx² + dy²). Math.sqrt gives a square root.
  distanceTo(other: Point): number {
    return 0
  }

  // 2. add(other): a NEW Point whose x and y are the sums.
  add(other: Point): Point {
    return new Point(0, 0)
  }
}

const p = new Point(0, 0)
const q = new Point(3, 4)
console.log(p.distanceTo(q), p.add(q))
`,
  solution: `class Point {
  x: number
  y: number

  constructor(x: number, y: number) {
    this.x = x
    this.y = y
  }

  distanceTo(other: Point): number {
    const dx = other.x - this.x
    const dy = other.y - this.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  add(other: Point): Point {
    return new Point(this.x + other.x, this.y + other.y)
  }
}

const p = new Point(0, 0)
const q = new Point(3, 4)
console.log(p.distanceTo(q), p.add(q))
`,
  tests: `
test("distance from (0,0) to (3,4) is 5", () => expect(new Point(0, 0).distanceTo(new Point(3, 4)), "distance").toBeCloseTo(5))
test("distance is the same both ways", () => expect(new Point(3, 4).distanceTo(new Point(0, 0)), "distance").toBeCloseTo(5))
test("distance from (1,1) to (4,5) is 5", () => expect(new Point(1, 1).distanceTo(new Point(4, 5)), "distance").toBeCloseTo(5))
test("add makes a new Point with summed coordinates", () => {
  const r = new Point(1, 2).add(new Point(10, 20))
  expect(r, "the result").toBeInstanceOf(Point)
  expect([r.x, r.y], "[x, y]").toEqual([11, 22])
})
`,
}
